import { preprocessPieChartData } from "@shared/utils/charts";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTheme } from "styled-components";
import { BacktestDetailsContext } from "@shared/features/bots/BacktestDetails.context";
import { sharedLib } from "@shared/services/shared.service";
import { isSymbol } from "@shared/types/utils";
import {
  AnalyticsPageName,
  BacktestingRunType,
  BacktestingStatus,
  BotType,
  DisplayResultMessage,
  GetBacktestingRunsRequest,
  LanguageNameSpaces,
} from "@shared/types";
import {
  removeBacktestingFromList,
  removeBotNameFromList,
} from "@shared/features/bots/utils";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { useDispatch } from "react-redux";
import { skipToken, SkipToken } from "@reduxjs/toolkit/query";
import { updateBacktestsData } from "@shared/features/bots/BotForm/Backtesting/utils";
import useFormValuesModified from "@shared/features/bots/BotForm/General/useFormValuesModified";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

export const usePnlStatusStatistic = () => {
  const theme = useTheme();
  const { liveBacktestingData } = useContext(BacktestDetailsContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);

  const data = liveBacktestingData?.pnl_status_to_counter;

  const assetToCounterData = useMemo(
    () =>
      Object.entries(data || {}).map(([status, value]) => ({
        name: status,
        value: value,
      })),
    [data]
  );

  const pieChartData = useMemo(
    () =>
      preprocessPieChartData(
        assetToCounterData,
        theme.colors.pieChartColors,
        true
      ),
    [assetToCounterData, theme]
  );

  const helperProps = {
    description: t("backtesting.helper_tooltips.pnl_status"),
    analyticsPageName: AnalyticsPageName.BACKTESTING,
    analyticsFieldName: "PnL Status",
    articleId: "9575281",
    title: t("backtesting.statistics.pnl_status"),
  };

  return {
    pieChartData,
    isEmpty: !data || !Object.keys(data).length,
    helperProps,
  };
};

export const useMostTradedCoinsStatistic = () => {
  const theme = useTheme();
  const { liveBacktestingData } = useContext(BacktestDetailsContext)!;

  const data = liveBacktestingData?.asset_to_counter;

  const assetToCounterData = useMemo(
    () =>
      Object.entries(data || {}).map(([asset, value]) => ({
        name: asset,
        value: value,
      })),
    [data]
  );

  const pieChartData = useMemo(
    () =>
      preprocessPieChartData(assetToCounterData, theme.colors.pieChartColors),
    [assetToCounterData, theme]
  );

  return {
    pieChartData,
    isEmpty: !data || !Object.keys(data).length,
  };
};

interface useConfirmDeleteModalProps {
  backtestingRun?: BacktestingRunType;
  closeModal: () => void;
  unapplyBacktest: () => void;
  queryBacktest?: number;
  displayResultMessage: DisplayResultMessage;
  isSeeAllScreen?: boolean;
  botType: BotType;
}
export const useConfirmDeleteModal = ({
  backtestingRun,
  closeModal,
  unapplyBacktest,
  queryBacktest,
  displayResultMessage,
  isSeeAllScreen,
  botType,
}: useConfirmDeleteModalProps) => {
  const dispatch = useDispatch();
  const [deleteBacktesting, { isLoading: deleteLoading }] =
    sharedLib.sharedApi.bots.useDeleteBacktestingRunMutation();
  const botFormArgs = useGetBacktestingRunsArgs();
  const seeAllArgs = useMemo(
    () => (botType ? { bot_type: botType } : skipToken),
    [botType]
  );
  const getBacktestingRunsArgs = isSeeAllScreen ? seeAllArgs : botFormArgs;

  const handleRemoveBacktestingFromList = () => {
    if (!backtestingRun) return;
    const getBotsNameArgs = {
      bot_type: botType,
      is_backtesting: true,
    };
    if (!isSymbol(getBacktestingRunsArgs)) {
      dispatch(
        //@ts-ignore
        removeBacktestingFromList(backtestingRun.id, getBacktestingRunsArgs)
      );
    }
    //@ts-ignore
    dispatch(removeBotNameFromList(backtestingRun.bot_id, getBotsNameArgs));
  };

  const handleDelete = () => {
    const backtestingId = backtestingRun?.id;
    if (backtestingId) {
      displayResultMessage(
        deleteBacktesting({ backtesting_run_id: backtestingId }).unwrap()
      )
        .then(() => {
          handleRemoveBacktestingFromList();
          closeModal();
          if (queryBacktest === backtestingId) {
            unapplyBacktest();
          }
        })
        .catch(() => {});
    }
  };

  return {
    handleDelete,
    deleteLoading,
  };
};

export const useGetBacktestingRuns = (
  args: GetBacktestingRunsRequest | void | SkipToken
) => {
  const dispatch = useDispatch();
  const {
    data: backtestingRunsData,
    currentData,
    isFetching,
    isUninitialized,
    isLoading,
    refetch,
  } = sharedLib.sharedApi.bots.useGetBacktestingRunsQuery(args, {
    refetchOnMountOrArgChange: true,
  });
  const [failedArgs, setFailedArgs] = useState<
    GetBacktestingRunsRequest | void | SkipToken
  >();
  const data = backtestingRunsData?.data;
  const botType = args && !isSymbol(args) ? args.bot_type : undefined;

  const inProgressRunsArgs = useMemo(() => {
    const inProgressRunsIds = currentData?.data
      ?.filter((backtest) => backtest.status === BacktestingStatus.IN_PROGRESS)
      .map((backtest) => backtest.id);
    return inProgressRunsIds?.length
      ? { backtesting_run_ids: inProgressRunsIds, bot_type: botType }
      : skipToken;
  }, [currentData, botType]);

  const {
    currentData: currentInProgressData,
    isFetching: isFetchingInProgress,
    isError,
  } = sharedLib.sharedApi.bots.useGetBacktestingRunsQuery(inProgressRunsArgs, {
    pollingInterval: inProgressRunsArgs === failedArgs ? 0 : 3000,
  });

  const inProgressRunsData = currentInProgressData?.data;

  useEffect(() => {
    if (isError) {
      setFailedArgs(args);
    } else if (failedArgs) {
      setFailedArgs(undefined);
    }
  }, [isError]);

  useEffect(() => {
    if (args !== skipToken && inProgressRunsData && !isFetchingInProgress) {
      dispatch(updateBacktestsData(args, inProgressRunsData) as any);
    }
  }, [inProgressRunsData, isFetchingInProgress]);

  return {
    data,
    isFetching,
    isUninitialized,
    isLoading,
    refetch,
    currentData,
  };
};

export const useGetBacktestingRunsArgs = () => {
  const { botType, account, subGroup, symbols } =
    useContext(BotFormContext) || {};
  const isSignalsBot = botType === BotType.SIGNAL;

  return useMemo(() => {
    if (!account || isSymbol(account)) return skipToken;
    let args: GetBacktestingRunsRequest | SkipToken = {
      bot_type: botType,
      account_ids: [account.client_id],
    };
    if (isSignalsBot) {
      args = subGroup ? { ...args, sub_group_ids: [subGroup.id] } : skipToken;
    } else {
      args =
        symbols && !isSymbol(symbols) && symbols.length
          ? { ...args, symbol: symbols[0] }
          : skipToken;
    }
    return args;
  }, [account, subGroup, isSignalsBot, symbols]);
};

interface UseBacktestingCardProps {
  isSelected?: boolean;
}

const useBacktestingCard = ({ isSelected }: UseBacktestingCardProps) => {
  const {
    backtestingValuesModified,
    setBacktestingValuesModified,
    backtestBotData,
  } = useContext(BotFormContext) || {};

  return useFormValuesModified({
    isSelected,
    valuesModified: backtestingValuesModified,
    setValuesModified: setBacktestingValuesModified,
    originalBotData: backtestBotData,
  });
};
export default useBacktestingCard;
